import React from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { hasDeliveryIssue, mapParcelStage } from '../utils/mapParcelStage';
import { DropdownOption, ParcelDropdown } from '../dropdown';
import { TabLabel, TrackingTab } from '../trackingtabs';
import { Tracking } from '~/lib/types/tracking-types';
import { P2GGrid, useP2GMediaQueryDown } from '@parcel2go/core-ui';
import { IP2GCoreComponentProps } from '@parcel2go/core-ui/dist/types';

const useStyles = makeStyles()((theme) => ({
  tabContainer: {
    paddingBottom: '4px',
    '& .p2g-text': {
      textTransform: 'none',
      fontWeight: '700'
    }
  },
  dropdown: {
    maxWidth: '250px',
    '& .MuiSelect-select': {
      paddingTop: '12px'
    }
  },
  selected: {
    height: '100%',
    backgroundColor: theme.palette.secondary.main,
    border: `2px solid ${theme.palette.secondary.main}`,
    color: '#fff',
    borderRadius: '4px',
    padding: '8px, 16px',
    '& .tab-icon': {
      height: '100%',
      width: '100%',
      '&>path': {
        fill: '#fff'
      }
    }
  },
  selectedWarning: {
    height: '100%',
    backgroundColor: '#B01515 !important',
    border: `3px solid #B01515`,
    color: '#fff',
    borderRadius: '4px',
    padding: '8px, 16px',
    '& .tab-icon': {
      height: '100%',
      width: '100%',
      '&>path': {
        fill: '#fff'
      }
    }
  },
  unselected: {
    height: '100%',
    border: `2px solid ${theme.palette.secondary.main}`,
    color: theme.palette.secondary.main,
    borderRadius: '4px',
    padding: '8px, 16px',
    '&:hover': {
      '& .tab-icon': {
        height: '100%',
        width: '100%',
        '&>path': {
          fill: '#fff'
        }
      }
    }
  },
  unselectedWarning: {
    height: '100%',
    border: '3px solid #B01515',
    color: '#B01515',
    borderRadius: '4px',
    padding: '8px, 16px',
    '& .tab-icon>path': {
      fill: '#B01515'
    },
    '&:hover': {
      backgroundColor: '#B01515 !important',
      '& .tab-icon': {
        fill: '#BO1515',
        height: '100%',
        width: '100%',
        '&>path': {
          fill: '#fff'
        }
      }
    }
  }
}));

export interface IParcelSelectorProps extends IP2GCoreComponentProps {
  parcelNumbers: string[];
  tracking: Tracking;
  handleTabClick: (value: string) => void;
  openTrackingNumber: string;
}

export const ParcelSelector: React.FC<IParcelSelectorProps> = (props) => {
  const { parcelNumbers, tracking } = props;
  const { classes } = useStyles();
  const isBreak = useP2GMediaQueryDown('sm');

  if (parcelNumbers.length > 5 || isBreak) {
    return (
      <P2GGrid item>
        <ParcelDropdown
          classNames={classes.dropdown}
          onChange={props.handleTabClick}
          id={''}
          value={props.openTrackingNumber}
          options={parcelNumbers.map((parcel, index) => {
            const parcelIndex = index + 1;
            const parcelData = tracking.parcels[parcel];
            const parcelStage = mapParcelStage(parcelData);

            return (
              <DropdownOption key={`${parcel}-option`} value={parcel} text={`${parcelIndex}`} stage={parcelStage} />
            );
          })}
        />
      </P2GGrid>
    );
  }

  return (
    <P2GGrid item>
      <P2GGrid container spacing={1} classNames={classes.tabContainer} wrap="nowrap">
        {parcelNumbers.map((parcel, index) => {
          const parcelIndex = index + 1;
          const parcelData = tracking.parcels[parcel];
          const parcelIssue = hasDeliveryIssue(parcelData);
          const notSelectedParcel = parcel !== props.openTrackingNumber;

          return (
            <TrackingTab
              key={`${parcel}-tab`}
              label={
                <TabLabel index={parcelIndex} data={parcelData} iconColor={notSelectedParcel ? '#B01515' : '#fff'} />
              }
              index={index}
              parcel={parcel}
              handleTabClick={props.handleTabClick}
              classNames={clsx(
                notSelectedParcel
                  ? parcelIssue
                    ? classes.unselectedWarning
                    : classes.unselected
                  : parcelIssue
                    ? classes.selectedWarning
                    : classes.selected
              )}
            />
          );
        })}
      </P2GGrid>
    </P2GGrid>
  );
};
